import { flightScheduleChangeMap } from './mappers/flightScheduleChangeMap'
import type { Order as OrderTypes } from '@luxuryescapes/contract-svc-order'
import type { operations as PaymentOperations } from '@luxuryescapes/contract-svc-payment'
import type { StripePaymentElementPaymentMethodType } from 'constants/payment'
import {
  REFUND_BACK_TO_ORIGIN_COMMENT,
  REFUND_DUE_TO_CHANGE_OF_MIND_REASON,
} from 'constants/payment'
import request from 'api/requestUtils'
import { getCruiseAmendmentOtpError } from 'lib/cruises/cruisePassengerAmendmentErrors'
import type { AmendCruisePassengersData, CruiseAmendmentSubmissionResult } from 'lib/cruises/cruisePassengerAmendmentTypes'
import qs from 'qs'
import { arrayToObject } from 'lib/array/arrayUtils'
import uuidV4 from 'lib/string/uuidV4Utils'
import asyncPoll from 'lib/promise/asyncPoll'
import delay from 'lib/utils/delay'
import { isFlightItemNotCompletedError, waitForCompletedFlightItem } from 'lib/flights/waitForCompletedFlightItem'
import { filterJourneySeatsToConfirmed } from 'lib/flights/reservationSeatAddons'
import { buildLegBaggageAddons } from 'lib/flights/reservationBaggageAddons'
import { getBrandLabel } from 'lib/whitelabels/whitelabels'
import { creditMap, mapOrderResponse, orderMap, refundMap, refundMetaMap, reservationMap } from './mappers/orderMap'
import config from 'constants/config'
import { getMerchantFeeDetails, getPaymentDetailsForOrders } from './payment'
import type { OrderItemType } from 'constants/order'
import { ItemOperation } from 'constants/order'
import { addGTMEvent } from './googleTagManager'
import { refundEvent } from 'analytics/eventDefinitions'
import { journeyV2Map } from './lib/journeyV2Map'
import type { FareType } from 'constants/flight'
import { getRefundRequestsByOrder } from './refundRequestFetch'
import { getOrderById, getStoredOrderById } from './orderLookup'
import { REFUND_REASONS } from 'constants/hotelCancellationRefundReasons'
import { CUSTOM_OFFER_CANCELLATION_REASON_COMMENT_BY_CODE } from 'checkout/Components/CheckoutSteps/Review/CustomOffers/CustomOfferCancellation/customOfferCancellationReasonCodes'
import requestLoyaltyOrderDetails from './loyalty/requestLoyaltyOrderDetails'
import type { ExperienceBookingType } from 'constants/experience'
import noop from 'lib/function/noop'
import { isAgentHubTravellerOrder } from 'lib/order/agentHubTravellerOrder'
import type { CreateOrderAccessInvitesRequest, FetchOrderAccessListQueryParams, ApiOrderAccessInviteStatus } from './types'
import { cruiseBookingMap } from './mappers/Cruise/cruiseMap'
import type { AvailableCredit } from './order.types'
import { reportClientError } from 'services/errorReportingService'

export type RecentOfferPurchaseType = 'hotel' | 'bedbank'

export interface RecentOfferPurchase {
  purchasedAt: string
  region: string
  brand: string
  /** Purchaser used LuxPlus+ pricing; older responses may omit this field. */
  isLuxPlusPurchase?: boolean
}

export function getRecentOfferPurchases(offerId: string, offerType: RecentOfferPurchaseType) {
  const query = new URLSearchParams({ offerId, offerType })

  return request.get<Array<RecentOfferPurchase>>(`/api/orders/recent-purchases?${query.toString()}`)
}

export interface MerchandiseOrderCreationItem {
  type: 'merchandise'
  transaction_key: string
  total: number
  shippingGroup: string
  variantId: string
  itemDiscounts: Array<App.Checkout.FormattedItemDiscount>
}

export interface MerchandiseShippingOrderCreationItem {
  type: 'merchandise_shipping'
  shippingGroup: string
  transaction_key: string
  total: number
  shippingGst?: number
  itemDiscounts?: Array<App.Checkout.FormattedItemDiscount>
  address: {
    /**
     * Primary address field
     * Where the main bulk of the address goes, e.g. 123 Fake street
     */
    address: string
    /**
     * Secondary address field
     * For things like apartment/unit/floor numbers
     */
    address2?: string
    city: string
    state: string
    country: string
    postcode: string
    phone: string
    firstName: string
    lastName: string
    company?: string
  }
}

export interface LoyaltyPurchasePointsOrderCreationItem {
  type: 'loyalty_points'
  transaction_key: string
  offerId: string
  total?: number
  basePrice: number
  basePoints: number
  bonusPoints: number
}

/**
 * Represent the interface required to create an order item for an experience to svc-order
 */
export interface ExperienceOrderCreationItem {
  origin?: OrderTypes.ExperienceOrigin
  item_discounts: Array<App.Checkout.FormattedItemDiscount>
  source_app: OrderTypes.SourceApp
  app_discount_amount?: number
  app_discount_percent?: number
  member_discount_amount?: number
  type: string
  id_experience_items?: string
  provider_offer_id: string
  meta_data?: Record<string, unknown>
  brand: App.BrandName
  transaction_key: string
  total: number
  extras?: Array<{
    id: string
    title: string
    quantity: number
    price: number
  }>
  language?: string
  categories: Array<App.ExperienceItemCategory>
  cancellation_policies?: {
    isFree: boolean
    timezoneOffset: string
    refundPolicies: Array<{
      id: string
      periods: Array<string>
      periodLabel: string
      groupId?: number
      value: number
    }>
    text: string
  }
  le_exclusive: boolean
  redemption_location_id?: string
  redemption_location_name?: string
  pickup_point_id?: string
  pickup_point_name?: string
  special_requests?: string
  traveller_info: {
    participants?: Array<{ [x: string]: any }>
    customer: any | null
  }
  ticket: {
    fareType: string
    identifier: string
    productId: string
    type: string
    ticketModesMap?: Record<string, string>
    bookingOptions?: {
      fromESimDataPackage?: string
      toESimDataPackage?: string
    }
    date?: string
    rateStartDate?: string
    rateEndDate?: string
    bookByDate?: string
    isExtra?: boolean
  }
  booking_start_date?: string
  title: string
  booking_type: ExperienceBookingType
  taxes_and_fees?: number
  ticketed: boolean
  is_accom_bundle?: string
  /**
   * Whether this bundled item belongs to an opt-in bundle (customer selected it and is charged on top
   * of the hotel rate) rather than a mandatory one (already priced into the rate).
   */
  bundle_optin?: boolean
  parent_cart_item_ids?: Array<string>
  complimentary?: boolean
  accommodation_parent_package_id?: string
}

type SubmittedAirportTransferOrderCreationItem = ExperienceOrderCreationItem & {
  deal_options?: {
    type?: string
    date?: string
    time?: string
    flight_number?: string
    adults?: number
    children?: number
    child_seats?: number
    booster_seats?: number
    bags?: number
    oversized_bags?: number
  }
  dropoff_point_id?: string
  dropoff_point_name?: string
  ticket: ExperienceOrderCreationItem['ticket'] & {
    day?: string
    time?: string
    surchargeAUD?: number
    surchargeIDR?: number
  }
}

function isAirportTransferOrderCreationItem(item: { deal_options?: { type?: string } }) {
  return item?.deal_options?.type === 'AIRPORT-TO-HOTEL' || item?.deal_options?.type === 'HOTEL-TO-AIRPORT'
}

function findCreatedAirportTransferItem(
  order: App.Order,
  submittedItem: SubmittedAirportTransferOrderCreationItem,
) {
  return order.transferItems.find(createdItem => (
    createdItem.experienceId === submittedItem.provider_offer_id &&
    createdItem.type === submittedItem.deal_options?.type
  )) ?? order.experienceItems.find(createdItem => (
    createdItem.experienceId === submittedItem.provider_offer_id &&
    (!submittedItem.deal_options?.flight_number || createdItem.flightNumber === submittedItem.deal_options.flight_number)
  ))
}

async function syncCreatedAirportTransferDetails(order: App.Order, submittedOrderData: any) {
  const submittedItems = (submittedOrderData?.items ?? []) as Array<SubmittedAirportTransferOrderCreationItem>
  const submittedTransferItems = submittedItems.filter(isAirportTransferOrderCreationItem)
  if (!submittedTransferItems.length) return

  const updateRequests = submittedTransferItems.map(async(submittedItem) => {
    const createdItem = findCreatedAirportTransferItem(order, submittedItem)
    if (!createdItem) return

    await request.put(
      `/api/orders/experiences/items/${createdItem.id}`,
      {
        op: 'update_airport_transfer',
        data: {
          flightNumber: submittedItem.deal_options?.flight_number ?? '',
          fromLocationName: submittedItem.pickup_point_name,
          fromLocationId: submittedItem.pickup_point_id,
          toLocationName: submittedItem.dropoff_point_name,
          toLocationId: submittedItem.dropoff_point_id,
          day: submittedItem.deal_options?.date ?? submittedItem.ticket?.day,
          time: submittedItem.deal_options?.time ?? submittedItem.ticket?.time ?? '',
          specialRequests: submittedItem.special_requests ?? '',
          adults: submittedItem.deal_options?.adults ?? 0,
          children: submittedItem.deal_options?.children ?? 0,
          childSeats: submittedItem.deal_options?.child_seats ?? 0,
          boosterSeats: submittedItem.deal_options?.booster_seats ?? 0,
          bags: submittedItem.deal_options?.bags ?? 0,
          oversizedBags: submittedItem.deal_options?.oversized_bags ?? 0,
          surchargeAUD: submittedItem.ticket?.surchargeAUD,
          surchargeIDR: submittedItem.ticket?.surchargeIDR,
        },
      },
      { credentials: 'include' },
    )
  })

  await Promise.all(updateRequests)
}

export type LegacyOrderItems = {
  _links: {
    self: {
      href: string
    }
    payments: {
      href: string
    }
  }
  id: string
  number: string
  transaction_key: string
  currency_code: string
  region_code: string
  status: string
  items: []
  brand: 'scooponexperience'
  total: number
  app_type: string
  is_gift: boolean
  gift_to: string
  gift_from: string
  gift_message: string
  customer_email: string
  customer_given_name: string
  customer_surname: string
  customer_full_name: string
  customer_phone_prefix: string
  customer_phone: string
  created_at: string
  updated_at: string
  utm_source: string
  utm_medium: string
  utm_campaign: string
  utm_content: string
  utm_term: string
  utm_ad: string
  utm_adgroup: string
  tracker_info: {
    payment_type: string
  }
}

export function getBrandOrders(memberId: string): Promise<Array<App.Order | App.AgentHubTravellerOrder>> {
  if (!memberId) {
    return Promise.resolve([])
  }

  const label = getBrandLabel(config.BRAND)
  if (!label) {
    return Promise.resolve([])
  }

  const brandOrderUri = `/api/orders?customer_id=${memberId}&status=!awaiting_purchase&per_page=50&without_brand=1&le_label=${label}`
  return request.get<App.ApiResponse<Array<unknown>>>(brandOrderUri, { credentials: 'include' }).then(response => response.result.map(order => mapOrderResponse(order, 'summary')))
}

/**
 * Use to detect if a customer has orders from the old www-ee-customer website
 */
export async function getLedLegacyOrders(memberId: string) {
  if (!memberId) {
    return {
      orders: [],
      legacyCutoffDate: null,
    }
  }

  const legacyOrdersPath = `/api/ee/orders?customer_id=${memberId}&old_led_orders=true&page=1`
  const response = await request.get<App.ApiResponse<Array<LegacyOrderItems>>>(legacyOrdersPath, { credentials: 'include' })

  return {
    orders: response.result,
    legacyCutoffDate: response.legacyCutoffDate,
  }
}

interface GetOrderParams {
  page?: number
  pageSize?: number
  status?: 'upcoming'
  sort?: 'asc' | 'desc'
  itemType?: OrderItemType,
  includeSharedOrders?: boolean,
}

const MagicUsersThatKillUsEverySixMonths = new Set([
  // The userWithCredits we use for e2e tests is a single user that ends up with thousands of orders before we clear it out
  // This starts to cause slow downs as we stream orders in during the course of it's e2e runs
  // To prevent this, we're going to identify this user and cap the number of orders returned to prevent slow downs
  '8c9433b0-5781-4b86-8139-43543b823f48',
  // This is the test production account, people use this to test purchases in production and has thousands of orders
  'e78297cc-0b82-466d-9453-86ea9074f053',
  // Used by an admin portal E2E, seems to be making about 10k orders per month
  'b7d3bee6-121e-4a74-ba50-33b8261a1067', // admin
  // LEBT user with central business account E2E
  'a135e72d-9b5c-4cdd-815e-ead73b9156e6', // customer-a674c1
  '1d4255dc-751b-418d-893f-50501b3149d2', // admin-3b39ad
  // Agent hub
  'b80dd74c-fef4-4a27-8cb1-4559b8324d6d', // e2e-agenthub-agent
  // not sure what these are ?
  '5d2c42e2-4001-4889-8267-166a616aa335', // notification-proxy-e2e
  '844e1c2c-9edf-4f4b-a35f-35f16e1e2394', // ci-user
  'a6b79b26-3063-4b5c-a847-ffb3b666a9da', // e2e user for society scenarios
  'c6a57532-e890-47b1-8b43-300a54b30947', // real point user for point payment tests
])

export function getOrders(
  memberId: string,
  region: string,
  params: GetOrderParams = {},
) {
  if (!memberId) {
    return Promise.resolve({
      orders: [] as Array<App.Order | App.AgentHubTravellerOrder>,
      total: 0,
    })
  }

  const query = qs.stringify({
    customer_id: memberId,
    status: '!awaiting_purchase',
    per_page: params.pageSize ?? 50,
    upcoming: params.status === 'upcoming',
    page: params.page,
    item_type: params.itemType,
    order_direction: params.sort,
    show_purchasing_customer_orders: true, // only active on LEBT
    include_shared_orders: params.includeSharedOrders,
  })

  return request.get<App.ApiResponse<Array<unknown>>>(
    `/api/orders?${query}`,
    { credentials: 'include' },
  ).then(async response => {
    const orders = response.result.map(order => mapOrderResponse(order, 'summary'))
    // cap the number of orders for our magic user with credits user
    const total = MagicUsersThatKillUsEverySixMonths.has(memberId) ? Math.min(orders.length, 20) : (response).total
    return {
      orders,
      total,
    }
  })
}

export interface FraudOrderData {
  id: string
  created_at: string
  fraud_score?: number | null
  fraud_desc?: string | null
}

export function getCustomerFraudOrders(memberId: string): Promise<Array<FraudOrderData>> {
  if (!memberId) {
    return Promise.resolve([])
  }

  const query = qs.stringify({
    customer_id: memberId,
    status: '!awaiting_purchase',
    per_page: 5,
    page: 1,
  })

  return request
    .get<App.ApiResponse<Array<FraudOrderData>>>(
      `/api/orders?${query}`,
      { credentials: 'include' },
    )
    .then(response => response.result ?? [])
}

export function getOrderItemsCancellationConfigs(orderId: string) {
  return request.get<App.ApiResponse<Record<string, App.CancellationConfig>>>(
    `/api/orders/${orderId}/cancellation-config?include_flights=true`,
    { credentials: 'include' },
  ).then(response => response.result)
}

export function getRefunds(orderId: string) {
  return request.get<App.ApiResponse<Array<OrderTypes.Refund>>>(
    `/api/orders/${orderId}/refunds`,
    { credentials: 'include' },
  ).then((response) => response.result.map(refundMap))
}

export interface ServerRefundMeta {
  id_refund_metadata: string
  updated_at: string
  created_at: string
  source: string
  refund_destination: string
  reason: string
  comment: string
  ticket_id: string
  cash_amount: string
  accounting_amount: string
  fk_orders: string
  charge_component_key: string
  reporting_service_key_uuid_item: string
  deleted_by: null
  refunded_by: string
  refund_fee: string
  additional_info: any
  fk_items: string
  transaction_key: string
}

export function getRefundMeta(orderId: string): Promise<Array<App.OrderRefundMeta>> {
  // Same mixed local/remote setup that getPaymentDetailsForOrders guards against: with a local
  // svc-order and a remote svc-payment, svc-payment validates the order id against the remote
  // svc-order before returning refund metadata and answers 422 for a locally created order,
  // which rejects the whole full-order fetch and blanks the My Escapes order page.
  if (config.APP_ENV === 'development' && config.SKIP_PAYMENT_DETAILS_FOR_ORDERS) {
    return Promise.resolve([])
  }

  return request.get<App.ApiResponse<Array<ServerRefundMeta>>>(
    `/api/refund_meta/${orderId}`,
    { credentials: 'include' },
  ).then((response) => response.result.map(refundMetaMap))
}

export function getOrderDatesRequest(orderId: string) {
  const datesRequestUri = `/api/orders/dates-request/order/${orderId}?activeOnly=true`
  return request.get<App.ApiResponse<Array<App.DatesRequest>>>(datesRequestUri, { credentials: 'include' }).then(response => {
    return response.result.map(datesRequest => datesRequestMap(datesRequest))
  })
}

export function getReservationDetail(itemId: string, orderId: string) {
  const uri = `/api/orders/${orderId}/items/${itemId}/reservation`

  return request.get<App.ApiResponse<OrderTypes.Reservation>>(uri, { credentials: 'include' })
    .then((response) => ({
      itemId,
      reservation: reservationMap(response.result, itemId),
      error: undefined,
    }))
    .catch((err) => ({
      itemId,
      reservation: undefined,
      error: {
        status: err.status,
        code: 'reservation_details_fetch_failed',
      },
    }))
}

function getAllReservationDetail(order: App.Order) {
  return Promise.all(
    order.items.filter(item => item.reservationMade)
      .map((item) =>
        getReservationDetail(item.id, order.id),
      ),
  )
}

export function getReservationDetailsForItems(
  items: Array<App.OrderItem>,
  orderId: string,
): Promise<Array<App.OrderReservation>> {
  const reservationPromises = items.map(item => getReservationDetail(item.id, orderId))
  return Promise.all(reservationPromises).then((results) => results.map(r => r.reservation).filter((res): res is App.OrderReservation => !!res))
}

export function createOrder(data: any, otpToken?: string) {
  const options = {
    credentials: 'include' as RequestCredentials,
    headers: {} as any,
  }

  if (otpToken) {
    options.headers.verification_token = otpToken
  }

  return request.post<App.ApiResponse<OrderTypes.Order>, unknown>('/api/orders', data, options)
    .then(async(response) => {
      if ((data?.items ?? []).some(isAirportTransferOrderCreationItem)) {
        const order = orderMap(response.result)
        await syncCreatedAirportTransferDetails(order, data).catch(noop)
        return getFullOrder(response.result.id)
      }

      const order = orderMap(response.result)
      const reservations = await getAllReservationDetail(order)
      const reservationsByItemId = arrayToObject(reservations, res => res.itemId)
      order.items = order.items.map(item => ({
        ...item,
        reservation: reservationsByItemId[item.id]?.reservation,
        reservationError: reservationsByItemId[item.id]?.error,
      }))
      return order
    })
}

export function finalizeOrder(orderId: string) {
  const uri = `/api/orders/${orderId}`

  return request.patch(uri, {
    op: 'finalize',
  }, { credentials: 'include' })
}

export function abandonOrder(orderId: string) {
  const uri = `/api/orders/${orderId}`

  return request.patch(uri, {
    op: 'abandon',
  }, { credentials: 'include' })
}

export function cancelOrderGiftingRequest(orderId: string) {
  const uri = `/api/orders/${orderId}`

  return request.patch(uri, {
    op: 'cancel_order_gift',
  }, { credentials: 'include' })
}

export interface RegenerateOrderPinResponse {
  pin: string
}

export function regenerateOrderPin(orderId: string): Promise<RegenerateOrderPinResponse> {
  return request.post<RegenerateOrderPinResponse, { surface: 'myescapes' }>(
    `/api/orders/${orderId}/pin-regenerations`,
    { surface: 'myescapes' },
    { credentials: 'include' },
  )
}

export function createItem(orderId: string, data: any) {
  const uri = `/api/orders/${orderId}/items`

  return request.post(uri, data, { credentials: 'include' })
}

export function updateItem(orderId: string, itemId: string, data: any) {
  const uri = `/api/orders/${orderId}/items/${itemId}`

  return request.put<App.ApiResponse<any>, unknown>(uri, data, { credentials: 'include' })
    .then((response) => reservationMap(response.result, itemId))
}

export function changeTourDates(orderId: string, itemId: string, data: any): Promise<App.Order> {
  return request.put<App.ApiResponse<OrderTypes.Order>, unknown>(`/api/orders/${orderId}/items/${itemId}`,
    { ...data, op: 'change_dates' },
    { credentials: 'include' },
  ).then((response) => orderMap(response.result))
}

export function changeArrivalDetails(orderId: string, itemId: string, data: any) {
  const uri = `/api/orders/${orderId}/items/${itemId}`

  data.op = 'change_arrival_details'
  return request.put<App.ApiResponse<any>, unknown>(uri, data, { credentials: 'include' })
    .then(async() => {
      const order = await getFullOrder(orderId)
      return order
    },
    )
}

export function changeReservationAddExtraGuests(orderId: string, itemId: string, data: any) {
  const uri = `/api/orders/${orderId}/items/${itemId}`

  return request.put<App.ApiResponse<any>, unknown>(uri, {
    ...data,
    op: 'add_extra_guests',
  }, { credentials: 'include' })
    .then((response) => reservationMap(response.result, itemId))
}

export function changeFlightDates(orderId: string, flightItemId: string, data: any) {
  const uri = `/api/orders/${orderId}/flights/${flightItemId}/addons`

  return request.post<App.ApiResponse<OrderTypes.Order>, unknown>(uri, {
    ...data,
    op: 'change_dates',
  }, { credentials: 'include' })
    .then((response) => {
      return orderMap(response.result)
    })
}

interface CreateCabinUpgradeAddonData {
  addons: Array<App.CabinUpgradeAddonInput>
  transaction_key: string
  payment_details: App.PaymentPlan
}

export function createCabinUpgradeAddon(orderId: string, flightItemId: string, data: CreateCabinUpgradeAddonData) {
  const uri = `/api/orders/${orderId}/flights/${flightItemId}/addons`

  return request.post<App.ApiResponse<OrderTypes.Order>, CreateCabinUpgradeAddonData & { op: 'cabin_upgrade' }>(uri, {
    ...data,
    op: 'cabin_upgrade',
  }, { credentials: 'include' })
    .then((response) => {
      return orderMap(response.result)
    })
}

/**
 * The fields svc-order actually validates on a flight-addon POST. A $0 seat change sends
 * `payments: []`; paid checkout still passes its fuller PaymentPlan, which is a superset.
 */
export type FlightAddonPaymentDetails = Pick<
  App.PaymentPlan,
  'payment_plan_key' | 'currency' | 'depositAmount' | 'payments'
>

interface CreateFlightAddonData {
  addons: Array<App.FlightAddonInput>
  transaction_key: string
  payment_details: FlightAddonPaymentDetails
}

/**
 * Assigns or changes seats on an existing flight booking.
 *
 * Unlike its siblings above this sends no `op`: svc-order registers one plain POST on this path
 * and never reads that field, so the addon `type` is what actually selects the behaviour.
 *
 * svc-order 400s unless the parent flight item is already completed. POST first, and only
 * wait-and-retry when that specific race fires — the usual My Escapes path is already stable.
 */
async function postFlightAddon(
  orderId: string,
  flightItemId: string,
  data: CreateFlightAddonData,
) {
  const uri = `/api/orders/${orderId}/flights/${flightItemId}/addons`
  const response = await request.post<App.ApiResponse<OrderTypes.Order>, CreateFlightAddonData>(uri, data, {
    credentials: 'include',
  })

  // 202 queues the addon job and returns the existing order. If that envelope is missing
  // fields orderMap expects, persist still happened — callers poll the transaction.
  if (!response?.result?.id) return undefined
  try {
    return orderMap(response.result)
  } catch {
    return undefined
  }
}

async function createFlightAddon(orderId: string, flightItemId: string, data: CreateFlightAddonData) {
  try {
    return await postFlightAddon(orderId, flightItemId, data)
  } catch(error) {
    if (!isFlightItemNotCompletedError(error)) throw error
    await waitForCompletedFlightItem(orderId, flightItemId)
    return await postFlightAddon(orderId, flightItemId, data)
  }
}

export async function createFlightSeatAddon(orderId: string, flightItemId: string, data: CreateFlightAddonData) {
  return await createFlightAddon(orderId, flightItemId, data)
}

/**
 * Buys extra checked baggage on an existing flight booking.
 *
 * Shares the flight-addon path and its `processing_addon` retry with seats; only the addon `type`
 * selects the behaviour. svc-flights validates the selections against the offers it cached when we
 * fetched them, so this must land inside `BAGGAGE_OFFER_WINDOW_MS` of that fetch.
 */
export async function createFlightBaggageAddon(
  orderId: string,
  flightItemId: string,
  data: CreateFlightAddonData,
) {
  return await createFlightAddon(orderId, flightItemId, data)
}

interface GetOrderWithPaymentsAndReservationsOpts {
  isLoyaltyEnabled?: boolean
  allowPartialEnrichment?: boolean
}

function getOrderEnrichmentValue<T>(
  result: PromiseSettledResult<T>,
  orderId: string,
  enrichment: string,
  fallback: T,
): T {
  if (result.status === 'fulfilled') {
    return result.value
  }

  reportClientError(result.reason, {
    context: 'order_enrichment_failed',
    enrichment,
    orderId,
  })
  return fallback
}

async function enrichCommercialOrder(order: App.Order, opts?: GetOrderWithPaymentsAndReservationsOpts): Promise<App.Order> {
  const loyaltyRequest = opts?.isLoyaltyEnabled ? requestLoyaltyOrderDetails(order.id, order.customerId) : Promise.resolve(undefined)
  const enrichmentRequests = [
    getPaymentDetailsForOrders([order.id], order.brand ?? config.BRAND),
    getRefunds(order.id),
    getRefundMeta(order.id),
    getAllReservationDetail(order),
    getRefundRequestsByOrder(order.id),
    getOrderDatesRequest(order.id),
    getMerchantFeeDetails(order.id),
    opts?.allowPartialEnrichment ? loyaltyRequest : loyaltyRequest.catch(noop),
  ] as const

  // Only checkout's post-completion fetch may return incomplete enrichment.
  if (!opts?.allowPartialEnrichment) {
    await Promise.all(enrichmentRequests)
  }

  const [
    paymentsResult,
    refundsResult,
    refundMetaResult,
    reservationsResult,
    refundRequestsResult,
    datesRequestsResult,
    merchantFeeDetailsResult,
    loyaltyOrderDataResult,
  ] = await Promise.allSettled(enrichmentRequests)

  const payments = getOrderEnrichmentValue(paymentsResult, order.id, 'payment_details', {})
  const refunds = getOrderEnrichmentValue(refundsResult, order.id, 'refunds', [])
  const refundMeta = getOrderEnrichmentValue(refundMetaResult, order.id, 'refund_metadata', [])
  const reservations = getOrderEnrichmentValue(reservationsResult, order.id, 'reservations', [])
  const refundRequests = getOrderEnrichmentValue(refundRequestsResult, order.id, 'refund_requests', [])
  const datesRequests = getOrderEnrichmentValue(datesRequestsResult, order.id, 'date_requests', [])
  const merchantFeeDetails = getOrderEnrichmentValue(merchantFeeDetailsResult, order.id, 'merchant_fee_details', [])
  const loyaltyOrderData = getOrderEnrichmentValue(loyaltyOrderDataResult, order.id, 'loyalty_order_details', undefined)

  const reservationsByItemId = arrayToObject(reservations, res => res.itemId)
  const refundMetaByItemId = arrayToObject(refundMeta, meta => meta.orderItemId)
  const paymentDetails = payments[order.id] ?? { payments: [] }
  return {
    ...order,
    type: 'full',
    depositDetails: paymentDetails.depositDetails,
    instalmentDetails: paymentDetails.instalmentDetails,
    reserveForZeroDetails: paymentDetails.deferredPaymentDetails,
    paymentScheduleDetails: paymentDetails.paymentScheduleDetails,
    groupPaymentDetails: paymentDetails.groupPaymentDetails,
    payments: paymentDetails.payments.filter(p => p.type !== 'manual'),
    refunds: refunds.map(refund => ({
      ...refund,
      meta: refundMetaByItemId[refund.itemId],
    })),
    items: order.items.map(item => ({
      ...item,
      reservation: reservationsByItemId[item.id]?.reservation,
      reservationError: reservationsByItemId[item.id]?.error,
    })),
    loyaltyDetails: {
      benefitsRedeemed: loyaltyOrderData?.benefitsRedeemed ?? [],
      pointsSpent: loyaltyOrderData?.pointsSpent ?? [],
      airportTransfer: loyaltyOrderData?.airportTransfer,
      pendingPoints: loyaltyOrderData?.pendingPoints,
      tier: order.loyaltyDetails.tier,
      esimBenefit: loyaltyOrderData?.esimBenefit,
    },
    merchantFees: merchantFeeDetails,
    refundRequests: arrayToObject(refundRequests, req => req.itemId),
    outsidePolicyDatesRequests: arrayToObject(datesRequests.filter(req => req.isOutsidePolicy), req => req.itemId),
  }
}

export function getFullOrder(id: string, opts?: GetOrderWithPaymentsAndReservationsOpts): Promise<App.Order> {
  return getOrderById(id).then((order) => enrichCommercialOrder(order, opts))
}

export async function getViewableOrder(id: string, opts?: GetOrderWithPaymentsAndReservationsOpts): Promise<App.Order | App.AgentHubTravellerOrder> {
  const order = await getStoredOrderById(id)
  if (isAgentHubTravellerOrder(order)) {
    return order
  }

  return enrichCommercialOrder(order, opts)
}

export function getOrderBySubscriptionId(id: string) {
  return request.get(`/api/orders/subscription-items?subscription_period_id=${id}`, { credentials: 'include' }).then((result: any) => {
    if (result) {
      return getFullOrder(result.fk_order_id)
    }
  })
}

export function getPublicOrderNotes(id: string) {
  return request.get<App.ApiResponse<Array<App.OrderComment>>>(`/api/orders/${id}/notes/public`, { credentials: 'include' })
}

export async function downloadOrderNoteAttachment(orderId: string, noteId: string) {
  const response: Blob = await request.get(`/api/orders/${orderId}/notes/${noteId}/attachment`, { credentials: 'include' })
  return response
}

export function getOrderTransactionStatus(id: string, transactionKey: string) {
  const uri = `/api/orders/${id}/transaction/${transactionKey}`

  return request.get(uri, { credentials: 'include' })
}

export function getSecurePaymentOrderTransactionStatus(paymentLinkId: string, orderId: string, transactionKey: string) {
  const uri = `/api/orders/secure-payments/${paymentLinkId}/orders/${orderId}/transaction/${transactionKey}`

  return request.get<OrderTypes.SecurePaymentOrderTransactionStatusResponse>(uri, { credentials: 'include' })
}

export function createSecurePurchaseCreditOrder(payload: OrderTypes.SecurePurchaseCreditOrderRequestBody): Promise<OrderTypes.SecurePurchaseCreditOrderResponse> {
  return request.post<OrderTypes.SecurePurchaseCreditOrderResponse, OrderTypes.SecurePurchaseCreditOrderRequestBody>(
    '/api/orders/secure-payments/purchase-credit',
    payload,
    { credentials: 'include', excludeBrand: true },
  )
}

export function convertOrderToBNBL(orderId: string, itemId: string) {
  const uri = `/api/orders/${orderId}/items/${itemId}`

  return request.put(uri, { op: 'remove_dates' }, { credentials: 'include' })
}

export function putTourOnHold(orderId: string) {
  const uri = `/api/orders/${orderId}`
  return request.patch<App.ApiResponse<OrderTypes.Order>, unknown>(uri, { op: 'put_tour_on_hold' }, { credentials: 'include' })
    .then((response) => orderMap(response.result))
}

export function requestPutTourOnHold(orderId: string) {
  const uri = `/api/orders/${orderId}`
  return request.patch<App.ApiResponse<OrderTypes.Order>, unknown>(uri, { op: 'request_put_tour_on_hold' }, { credentials: 'include' })
    .then((response) => orderMap(response.result))
}

export interface createDatesRequestParams {
  orderId: string
  itemId: string
  numberOfAdults: number
  numberOfTeenagers: number
  numberOfChildren: number
  numberOfInfants: number
  requestDate: string
  isChangeDates: boolean
  isOutsidePolicy?: boolean
  reason?: string
  reasonType?: string
  attachments?: Array<string>
  requestedNumberOfNights?: number
  requestedPackageId?: string
  requestedRoomRateId?: string
}

export function uploadChangeDatesRequestAttachment(orderId: string, itemId: string, attachments?: Array<File>) {
  const data = new FormData()
  data.append('orderId', orderId)
  data.append('itemId', itemId)
  if (attachments && attachments?.length > 0) {
    attachments.forEach((file) => {
      data.append('attachments', file)
    })
  }

  return request
    .post<App.ApiResponse<{ attachments: Array<string> }>, any>('/api/orders/dates-request/attachment',
      data,
      { credentials: 'include' },
    ).then((response) => response.result)
}

export function postDatesRequest(body: createDatesRequestParams) {
  const uri = '/api/orders/dates-request'
  return request.post(uri, body, { credentials: 'include' })
}

export function updateDatesRequest(datesRequestId: string, status: string) {
  const uri = `/api/orders/dates-request/${datesRequestId}`

  return request.patch<App.ApiResponse<App.DatesRequest>, unknown>(uri, { status }, { credentials: 'include' })
    .then((response) => datesRequestMap(response.result))
}

interface RefundRoomParams {
  orderId: string
  itemId: string
  roomId?: string
  transactionKey?: string
  method: App.RefundMethod
  reason: string
  offerId: string
  applyBonusCredit?: boolean
  customerRefundReason?: {
    option?: string
    text?: string
  }
  willRefundInsurance?: boolean
  isBedbankRoom?: boolean
  refund_sources?: Array<App.RefundRequestSource>
}

export function refundRoom({
  orderId,
  itemId,
  roomId,
  method,
  reason,
  customerRefundReason,
  transactionKey = uuidV4(),
  offerId,
  applyBonusCredit,
  willRefundInsurance,
  isBedbankRoom,
  refund_sources,
}: RefundRoomParams): Promise<App.Order> {
  let comment = REFUND_BACK_TO_ORIGIN_COMMENT
  if (customerRefundReason?.option) {
    const { option, text } = customerRefundReason
    if (text) {
      // Use 'Other' format for any option that has a free text comment
      comment = `Other - {${text}}`
    } else {
      comment = option
    }
  }

  const query = qs.stringify({ room_id: roomId })
  const data: App.RefundRequestData = {
    op: 'refund',
    transaction_key: transactionKey,
    reason,
    comment,
    method,
    applyBonusCredit,
  }
  if (refund_sources?.length) {
    data.refund_sources = refund_sources
  }
  return request.put(`/api/orders/${orderId}/items/${itemId}?${query}`, data, { credentials: 'include' }).then(async(res: App.ApiResponse) => {
    await asyncPoll<App.Order>({
      validateFunction: async(order) => {
        const allItems = [...order.items, ...order.bedbankItems, ...order.carHireItems]
        const itemUpdated = !!allItems.find(updatedItem => updatedItem.id === itemId && updatedItem.status === 'cancelled')

        if (willRefundInsurance) {
          const insuranceUpdated = order.insuranceItems.every(item => item.status === 'cancelled')
          return itemUpdated && insuranceUpdated
        }

        return itemUpdated
      },
      apiCall: () => getOrderById(orderId),
      maxTime: isBedbankRoom ? 120000 : 30000,
      pollInterval: isBedbankRoom ? 2000 : 500,
    })

    const order = await getFullOrder(orderId)
    if (res.message === 'accepted') {
      addGTMEvent(refundEvent(order, [offerId], method))
    }
    return order
  })
}

export interface RefundInsuranceParams {
  orderId: string
  itemId: string
  transactionKey?: string
  method: App.RefundMethod
}

export async function refundInsurance(
  { orderId, itemId, method, transactionKey = uuidV4() }: RefundInsuranceParams,
): Promise<App.Order> {
  const uri = `/api/orders/${orderId}/items/${itemId}`
  const data: App.RefundRequestData = {
    op: 'refund',
    transaction_key: transactionKey,
    reason: REFUND_DUE_TO_CHANGE_OF_MIND_REASON,
    comment: REFUND_BACK_TO_ORIGIN_COMMENT,
    method,
  }

  const refundResponse = await request.put<App.ApiResponse<null>, App.RefundRequestData>(uri, data, { credentials: 'include' })
  await delay(100)

  // when we call refundInsurance, svc-order creates a few jobs, enqueues them, and then send the response.
  // So sometimes we call getOrderWithPaymentsAndReservations but some of these jobs could not be finished yet.
  // When this happens we need to wait a few moments and call getOrderWithPaymentsAndReservations again.
  // This function does it
  await asyncPoll<App.Order>({
    validateFunction: async(order) => {
      const item = order.insuranceItems.find(updatedItem => updatedItem.id === itemId)
      return !!item && item.status === 'cancelled'
    },
    apiCall: () => getOrderById(orderId),
    maxTime: 30000,
    pollInterval: 500,
  })

  const updatedOrder = await getFullOrder(orderId)
  if (refundResponse.message === 'accepted') {
    addGTMEvent(refundEvent(updatedOrder, [itemId], method))
  }

  // @ts-ignore
  return updatedOrder
}

export async function refundSubscriptionItem(
  orderId: string,
  itemId: string,
  method: App.RefundMethod,
  options: {
    reason?: string
    comment?: string
    transactionKey?: string
  } = {},
) {
  const {
    transactionKey = uuidV4(),
    comment = REFUND_BACK_TO_ORIGIN_COMMENT,
    reason = 'Other',
  } = options
  const data: App.RefundRequestData = {
    op: 'refund',
    transaction_key: transactionKey,
    reason,
    comment,
    method,
  }

  return request.put(`/api/orders/${orderId}/items/${itemId}`, data, { credentials: 'include' }).then(async() => {
    // Sending a refund op only queues a job, we need to then poll until we see it be complete
    await asyncPoll({
      validateFunction: async(order: App.Order) => {
        const item = order.subscriptionItems.find(item => item.id === itemId)
        return item?.status === 'cancelled'
      },
      apiCall: () => getOrderById(orderId),
      maxTime: 30000,
      pollInterval: 500,
    })
  })
}

interface RefundAddonsParams {
  orderId: string
  addonIds: Array<string>
  method: App.RefundMethod
}

export interface RefundBookingProtectionParams {
  orderId: string
  itemId: string
  transactionKey?: string
  method: App.RefundMethod
}

export async function refundBookingProtection(
  { orderId, itemId, method, transactionKey = uuidV4() }: RefundBookingProtectionParams,
): Promise<App.Order> {
  const uri = `/api/orders/${orderId}/items/${itemId}`
  const data: App.RefundRequestData = {
    op: 'refund',
    transaction_key: transactionKey,
    reason: REFUND_DUE_TO_CHANGE_OF_MIND_REASON,
    comment: REFUND_BACK_TO_ORIGIN_COMMENT,
    method,
  }

  const refundResponse = await request.put<App.ApiResponse<null>, App.RefundRequestData>(uri, data, { credentials: 'include' })
  await delay(100)

  // when we call refundBookingProtection, svc-order creates a few jobs, enqueues them, and then send the response.
  // So sometimes we call getOrderWithPaymentsAndReservations but some of these jobs could not be finished yet.
  // When this happens we need to wait a few moments and call getOrderWithPaymentsAndReservations again.
  // This function does it
  await asyncPoll<App.Order>({
    validateFunction: async(order) => {
      const item = order.bookingProtectionItems.find((updatedItem: App.OrderBookingProtectionItem) => updatedItem.id === itemId)
      return !!item && item.status === 'cancelled'
    },
    apiCall: () => getOrderById(orderId),
    maxTime: 30000,
    pollInterval: 500,
  })

  const updatedOrder = await getFullOrder(orderId)

  if (refundResponse.message === 'accepted') {
    addGTMEvent(refundEvent(updatedOrder, [itemId], method))
  }

  return updatedOrder
}

export async function refundAddons(
  { orderId, addonIds, method }: RefundAddonsParams,
): Promise<App.Order> {
  const uri = `/api/orders/${orderId}/items`
  const data: App.RefundRequestData = {
    op: 'refund',
    items: addonIds.map(id => ({
      item_id: id,
      transaction_key: uuidV4(),
    })),
    reason: REFUND_DUE_TO_CHANGE_OF_MIND_REASON,
    comment: REFUND_BACK_TO_ORIGIN_COMMENT,
    method,
  }

  const refundResponse = await request.patch<App.ApiResponse<null>, App.RefundRequestData>(uri, data, { credentials: 'include' })
  await delay(100)

  // when we call refundAddons, svc-order creates a few jobs, enqueues them, and then send the response.
  // So sometimes we call getOrderWithPaymentsAndReservations but some of these jobs could not be finished yet.
  // When this happens we need to wait a few moments and call getOrderWithPaymentsAndReservations again.
  // This function does it
  await asyncPoll<App.Order>({
    validateFunction: async(updatedOrder) => addonIds.every(addonId => {
      const updatedAddon =
        updatedOrder.addonItems.find(updatedAddon => updatedAddon.id === addonId) ||
        updatedOrder.experienceItems.find(updatedExperience => updatedExperience.id === addonId) ||
        updatedOrder.transferItems.find(updatedTransfer => updatedTransfer.id === addonId)

      return updatedAddon?.status === 'cancelled'
    }),
    apiCall: () => getOrderById(orderId),
    maxTime: 30000,
    pollInterval: 500,
  })

  const updatedOrder = await getFullOrder(orderId)

  if (refundResponse.message === 'accepted') {
    addGTMEvent(refundEvent(updatedOrder, addonIds, method))
  }

  // @ts-ignore
  return updatedOrder
}

export interface RefundFlightParams {
  orderId: string
  itemId: string
  transactionKey?: string
  method: App.RefundMethod
  legId?: string
}

function isFlightItemCancelled(order: App.Order, itemId: string): boolean {
  const item = order.flightItems.find(updatedItem => updatedItem.id === itemId)
  return !!item && item.status === 'cancelled'
}

export async function refundFlight(
  { orderId, itemId, method, legId, transactionKey = uuidV4() }: RefundFlightParams,
): Promise<App.Order> {
  const uri = `/api/orders/${orderId}/items/${itemId}`
  const data: App.RefundRequestData = {
    op: ItemOperation.REFUND,
    transaction_key: transactionKey,
    reason: REFUND_DUE_TO_CHANGE_OF_MIND_REASON,
    comment: REFUND_BACK_TO_ORIGIN_COMMENT,
    method,
  }
  if (legId) {
    data.flight_leg_id = legId
  }

  const refundResponse = await request.put<App.ApiResponse<null>, App.RefundRequestData>(uri, data, { credentials: 'include' })
  await delay(100)

  // when we call refundFlight, svc-order creates a few jobs, enqueues them, and then send the response.
  // So sometimes we call getOrderWithPaymentsAndReservations but some of these jobs could not be finished yet.
  // When this happens we need to wait a few moments and call getOrderWithPaymentsAndReservations again.
  // This function does it
  if (legId) {
    await asyncPoll<App.FlightOrderItemDetails>({
      validateFunction: async(flightDetails) =>
        flightDetails.bookingJourney.legs.find(leg => leg.id === legId)?.status === 'cancelled',
      apiCall: () => getFlightDetailsWithDedup({ orderId, itemId, skipCache: true }),
      maxTime: 120000,
      pollInterval: 2000,
    })
  } else {
    await asyncPoll<App.Order>({
      validateFunction: async(order) => isFlightItemCancelled(order, itemId),
      apiCall: () => getOrderById(orderId),
      maxTime: 120000,
      pollInterval: 5000,
    })
  }

  const updatedOrder = await getFullOrder(orderId)
  if (refundResponse.message === 'accepted') {
    addGTMEvent(refundEvent(updatedOrder, [itemId], method))
  }

  // @ts-ignore
  return updatedOrder
}

export function cancelCruiseItem(orderId: string, itemId: string) {
  return request.patch<App.ApiResponse<undefined>, undefined>(
    `/api/orders/${orderId}/cruises/${itemId}/refund`,
    undefined,
    { credentials: 'include' },
  )
}

interface ServerRefundInfo {
  item_metadata: {
    charge_component_key: string
    cash_amount: number
    accounting_amount: number
    promo_amount: number
    voucher_amount?: number
  }
  surcharge_metadata?: {
    charge_component_key: string
    cash_amount: number
    accounting_amount: number
  }
  business_credit_refund_amount: number
  merchant_fee_amount?: number
  is_partially_paid_order: boolean
  bonus_credit: number
  insurance_metadata: {
    is_refundable: boolean
    accounting_amount: number
    cancel_insurance: boolean
  }
}

export interface StripePaymentIntentPayload {
  amount: number
  currency: string
  metadata: {
    orderId: string
    saveCard?: boolean
    paymentMethod?: string
  }
}

export interface StripeValidateBinPayload {
  orderId: string
  paymentMethodId: string
  isV2?: boolean
}

export interface StripePaymentElementPaymentIntentPayload {
  amount: number
  currency: string
  brand?: string
  paymentMethodType: StripePaymentElementPaymentMethodType
  metadata: {
    orderId: string
    isSavedCard: boolean
    isDeposit: boolean
    shouldUpdateDefaultPaymentMethod: boolean
    isNewCard: boolean
    shouldSaveCard: boolean
    isDeferredPayment: boolean
    isDepositBalance: boolean
    instalmentPaymentType?: string
    paymentMethodId?: string
    productType: string
    userAgent?: string
    ipAddress?: string
    shouldSaveCardOffSession?: boolean
    isPayAtPropertyOrder?: boolean
    region?: string
    offSessionCardPurpose?: string
    offSessionCardPurposeId?: string
    shouldAppendBackupOffSessionCard?: boolean
    isSecurePayment?: boolean
    paymentScheduleChargeId?: string
    isRefundable?: boolean
    chargeId?: string
    productId?: string
    orderTotal?: number
    isExpressCheckout?: boolean
    agentRequest3ds?: 'true' | 'false' | 'not_asked'
    checkoutPaymentId?: string
  }
  paymentLinkId?: string
  merchantFeeInfo?: {
    amount: number
    paymentType?: string
    shouldAutoRefund?: boolean
  }
}

export interface StripePaymentElementSetupIntentPayload {
  currency: string
  paymentMethodType: StripePaymentElementPaymentMethodType
  metadata: {
    orderId: string
    isSavedCard: boolean
    isDeposit: boolean
    shouldUpdateDefaultPaymentMethod: boolean
    isNewCard: boolean
    shouldSaveCard: boolean
    isDeferredPayment: boolean
    isDepositBalance: boolean
    instalmentPaymentType?: string
    paymentMethodId?: string
    productType: string
    shouldSaveCardOffSession?: boolean
    isPayAtPropertyOrder?: boolean
    region?: string
    offSessionCardPurpose?: string
    offSessionCardPurposeId?: string
    shouldAppendBackupOffSessionCard?: boolean
    paymentScheduleChargeId?: string
  }
}

export interface BridgerPayCashierPayload {
  order_id: string
  currency: string
  country: string
  amount: number
  first_name: string
  last_name: string
  phone: string
  order_details?: BridgerPayCashierAdditionalData | {}
}

export interface BridgerPayCashierAdditionalData {
  event_dates?: Array<string>
  departure_dates?: Array<string>
  checkin_dates?: Array<string>
  destinations?: Array<string>
  customer_name?: string
  passenger_names?: Array<string>
  flight_carrier_codes?: Array<string>
  items: Array<{
    name: string
    quantity: number
    amount: number
    currency: string
  }>
}

export function getPossibleRoomRefundTotal(orderId: string, itemId: string, roomId?: string): Promise<App.RefundInfo> {
  const queryParams = qs.stringify({ room_id: roomId, refund_type: 'auto' })

  return request.get<App.ApiResponse<ServerRefundInfo>>(
    `/api/orders/${orderId}/items/${itemId}/refund?${queryParams.toString()}`,
    { credentials: 'include' },
  ).then<App.RefundInfo>(response => {
    const result = response.result
    const willRefundInsurance = result.insurance_metadata.cancel_insurance && result.insurance_metadata.is_refundable
    const itemRefundAmount = result.is_partially_paid_order ? result.item_metadata.cash_amount : result.item_metadata.accounting_amount
    const surchargeRefundAmount = result.is_partially_paid_order ? (result.surcharge_metadata?.cash_amount || 0) : (result.surcharge_metadata?.accounting_amount || 0)
    const merchantFeeRefundAmount = result.merchant_fee_amount || 0
    const promoRefundAmount = result.item_metadata?.promo_amount || 0
    const refundableAmount = itemRefundAmount + surchargeRefundAmount + merchantFeeRefundAmount - promoRefundAmount
    return {
      refundableAmount,
      groupPaymentAllocationComponents: {
        itemAmount: result.item_metadata.cash_amount,
        surchargeAmount: result.surcharge_metadata ? result.surcharge_metadata.cash_amount : 0,
        merchantFeeAmount: result.merchant_fee_amount ?? 0,
      },
      refundableMerchantFeeAmount: result.merchant_fee_amount || 0,
      bonusCreditAmount: result.bonus_credit || 0,
      promoAmount: result.item_metadata.promo_amount || 0,
      voucherAmount: result.item_metadata.voucher_amount || 0,
      insurance: {
        willCancel: result.insurance_metadata.cancel_insurance ?? false,
        isRefundable: result.insurance_metadata.is_refundable ?? false,
        refundAmount: willRefundInsurance ? result.insurance_metadata.accounting_amount ?? 0 : 0,
      },
    }
  }).catch((): App.RefundInfo => {
    return {
      refundableAmount: 0,
      refundableMerchantFeeAmount: 0,
      bonusCreditAmount: 0,
      promoAmount: 0,
      voucherAmount: 0,
      insurance: {
        willCancel: false,
        isRefundable: false,
        refundAmount: 0,
      },
    }
  })
}

export function createLeCreditTransaction(data: any) {
  return request.post('/api/buy_with_credit/checkout', data, { credentials: 'include' })
}

export function createBraintreeTransaction(data: any) {
  return request.post('/api/braintree/checkout', data, { credentials: 'include' })
}

export function getBraintreeCredentials() {
  return request.get('/api/braintree/credentials', { credentials: 'include' })
}

export function getStripeCredentials(currency: string) {
  const queryParams = new URLSearchParams({ currency })
  return request.get<App.ApiResponse<any>>(`/api/stripe/credentials?${queryParams.toString()}`, { credentials: 'include' }).then(response => response.result)
}

export function getRazorpayCredentials() {
  return request.get<App.ApiResponse<any>>('/api/razorpay/credentials', { credentials: 'include' })
}

export function getBridgerPayCashier(data: BridgerPayCashierPayload, paymentType: string) {
  return request.post<App.ApiResponse<any>, any>(`/api/bridgerpay/cashier?payment_type=${paymentType}`, data, { credentials: 'include' }).then(response => response.result)
}

export function getStripePaymentIntentSecret(data: StripePaymentIntentPayload) {
  return request.post<App.ApiResponse<any>, any>('/api/stripe/payment-intent/create', data, { credentials: 'include' }).then(response => response.result)
}

export function getStripeSetupIntentSecret(data: StripePaymentIntentPayload) {
  return request.post<App.ApiResponse<any>, any>('/api/stripe/setup-intent/deferred-payment', data, { credentials: 'include' }).then(response => response.result)
}

export async function stripeValidateBin(data: StripeValidateBinPayload) {
  return request.post<App.ApiResponse<void>, StripeValidateBinPayload>('/api/payments/v2/stripe/validate-bin', data, { credentials: 'include' }).then(response => response.result)
}

interface StripePaymentElementSetupIntentResponse {
  clientSecret?: string
  ephemeralKey: string
  customerID?: string
  ephemeralKeySecret: string
}

interface StripePaymentElementPaymentIntentResponse {
  clientSecret: string
}

interface StripePaymentElementPaymentIntentOptions {
  agencyId?: string
}

export async function getStripePaymentElementPaymentIntentSecret(data: StripePaymentElementPaymentIntentPayload, options?: StripePaymentElementPaymentIntentOptions) {
  const query = options?.agencyId ? `?agencyId=${encodeURIComponent(options.agencyId)}` : ''

  return request.post<App.ApiResponse<StripePaymentElementPaymentIntentResponse>, StripePaymentElementPaymentIntentPayload>(
    `/api/payments/v2/stripe/payment-intents${query}`,
    data,
    { credentials: 'include', excludeBrand: !!data.brand },
  ).then(response => response.result)
}

export async function getStripePaymentElementSetupIntentSecret(data: StripePaymentElementSetupIntentPayload) {
  return request.post<App.ApiResponse<StripePaymentElementSetupIntentResponse>, StripePaymentElementSetupIntentPayload>('/api/payments/v2/stripe/setup-intents', data, { credentials: 'include' }).then(response => response.result)
}

type SplitCardValidationSetupIntentOperation = PaymentOperations['createCardValidationSetupIntent']
export type SplitCardValidationSetupIntentPayload = SplitCardValidationSetupIntentOperation['requestBody']['content']['application/json']
type SplitCardValidationSetupIntentResponse = SplitCardValidationSetupIntentOperation['responses']['200']['content']['application/json']
export type SplitCardValidationSetupIntentResult = SplitCardValidationSetupIntentResponse['result']

export async function createSplitCardValidationSetupIntent(data: SplitCardValidationSetupIntentPayload) {
  return request.post<SplitCardValidationSetupIntentResponse, SplitCardValidationSetupIntentPayload>(
    '/api/payments/v2/stripe/setup-intents/card-validation',
    data,
    { credentials: 'include', excludeBrand: true },
  ).then(response => response.result)
}

export interface KlarnaPayloadClient {
  attachment: {
    body: string
  }
  locale: string
  purchaseCountry: string
  purchaseCurrency: string
  // billingAddress: {
  //   email: string;
  //   familyName: string;
  //   givenName: string;
  //   phone?: string;
  // },
  customer: {
    dateOfBirth?: string
  }
  orderAmount: number
  orderTaxAmount: number
  orderLines: Array<{
    type: string
    reference: string
    name: string
    quantity: number
    unitPrice: number
    taxRate: number
    totalAmount: number
    totalDiscountAmount: number
    totalTaxAmount: number
    productUrl: string
    imageUrl: string
  }>
}

export interface KlarnaPayloadServer {
  attachment: {
    body: string
  }
  locale: string
  purchase_country: string
  purchase_currency: string
  // billing_address: {
  //   email: string;
  //   family_name: string;
  //   given_name: string;
  //   phone?: string;
  // }
  customer: {
    date_of_birth?: string
  }
  order_amount: number
  order_tax_amount: number
  order_lines: Array<{
    type: string
    reference: string
    name: string
    quantity: number
    unit_price: number
    tax_rate: number
    total_amount: number
    total_discount_amount: number
    total_tax_amount: number
    product_url: string
    image_url: string
  }>
}

export interface KlarnaCredentials {
  client_token: string
  session_id: string
  payment_method_categories: Array<{
    identifier: string
    name: string
    asset_urls: {
      descriptive: string
      standard: string
    }
  }>
}

export interface KlarnaSession {
  clientToken: string
  paymentMethod: string
  id: string
}

export function createKlarnaSession(data: KlarnaPayloadClient): Promise<KlarnaSession> {
  return request
    .post<App.ApiResponse<KlarnaCredentials>, KlarnaPayloadServer>(
      '/api/klarna/credentials',
      transformKlarnaPayloadToServer(data),
      { credentials: 'include' },
    ).then(response => ({
      clientToken: response.result.client_token,
      paymentMethod: response.result.payment_method_categories[0].identifier,
      id: response.result.session_id,
    }))
}

export function updateKlarnaSession(id: string, data: KlarnaPayloadClient): Promise<App.ApiResponse<void>> {
  return request
    .put<App.ApiResponse<void>, KlarnaPayloadServer>(
      `/api/klarna/session/${id}`,
      transformKlarnaPayloadToServer(data),
      { credentials: 'include' },
    )
}

export function getSavedCardsFromStripe(customer_id: string) {
  return request.get<App.ApiResponse<Array<any>>>(`/api/stripe/${customer_id}/cards`, { credentials: 'include' }).then(response => response.result)
}

export function getSavedCardsOnStripe(customer_id: string) {
  return request.get<App.ApiResponse<Array<App.StripePaymentCard>>>(`/api/stripe/${customer_id}/saved-cards`, { credentials: 'include' }).then(response => response.result)
}

export function getPaymentMethodFromStripe(payment_method_id: string, currency: string) {
  return request.get<App.ApiResponse<App.StripePaymentCard>>(`/api/stripe/payment-method/${payment_method_id}?currency=${currency}`, { credentials: 'include' }).then(response => response.result)
}

export function createPendingLatitudeTransaction(data: any) {
  return request.post('/api/latitude/checkout/initiate', data, { credentials: 'include' })
}

export function createStripeTransaction(data: any) {
  return request.post('/api/stripe/checkout', data, { credentials: 'include' })
}

export function createRazorpayTransaction(data: any) {
  return request.post('/api/razorpay/checkout', data, { credentials: 'include' })
}

export interface KlarnaTransactionRequestClient {
  authorization_token: string
  currency: string
  fk_orders: string
  supplementary_data: KlarnaPayloadClient
  item_ids: Array<string>
}

export interface KlarnaTransactionRequestServer {
  authorization_token: string
  currency: string
  fk_orders: string
  supplementary_data: KlarnaPayloadServer
}

export interface KlarnaTransaction {
  clientToken: string
  paymentMethod: string
}

export function createKlarnaTransaction(data: KlarnaTransactionRequestClient): Promise<KlarnaTransaction> {
  return request
    .post<App.ApiResponse<KlarnaTransaction>, KlarnaTransactionRequestServer>(
      '/api/klarna/checkout',
      {
        ...data,
        supplementary_data: transformKlarnaPayloadToServer(data.supplementary_data),
      },
      { credentials: 'include' },
    ).then(response => response.result)
}

export function createPromoTransaction(data: any) {
  return request.post('/api/promo/checkout', data, { credentials: 'include' })
}

export function getLatitudeApplyRequest() {
  return request.get<App.ApiResponse<{ [key: string]: string }>>('/api/latitude/checkout/application-request', { credentials: 'include' })
}

export function resendConfirmationForTransaction(orderId: string, offerId?: string) {
  return request.post(`/api/orders/${orderId}/resend`, { id_offer: offerId }, {
    credentials: 'include',
  })
}

export type FlightElectronicTicketRoute =
  | 'self_service_resend'
  | 'self_service_resend_pending'
  | 'support_case'
  | 'jetstar_booking_confirmation'

export type FlightElectronicTicketRequestSource = 'my_escapes' | 'support_assistant'

export type FlightElectronicTicketSendResult = {
  message: string
  route: FlightElectronicTicketRoute
}

function mapSendFlightElectronicTicketResponse(
  response: App.ApiResponse<FlightElectronicTicketSendResult> & Partial<FlightElectronicTicketSendResult>,
): FlightElectronicTicketSendResult {
  if (response.result) {
    return response.result
  }

  return {
    message: response.message ?? 'success',
    route: response.route!,
  }
}

export function sendFlightElectronicTicket(
  orderId: string,
  itemId: string,
  options: {
    emailRecipients?: Array<string>
    source?: FlightElectronicTicketRequestSource
  } = {},
) {
  const source = options.source ?? 'my_escapes'

  return request.post<
    App.ApiResponse<FlightElectronicTicketSendResult> & Partial<FlightElectronicTicketSendResult>,
    Record<string, unknown>
  >(
    `/api/orders/${orderId}/items/${itemId}/flight-details`,
    {
      op: 'send_flight_electronic_tickets',
      source,
      ...(options.emailRecipients?.length ? { email_recipients: options.emailRecipients } : {}),
    },
    { credentials: 'include' },
  ).then(mapSendFlightElectronicTicketResponse)
}

export function getAvailableCredit(memberId: string, currency = 'AUD') {
  const uri = `/api/credits?id_member=${memberId}&currency=${currency}`
  return request.get<App.ApiResponse<Array<unknown>>>(uri, { credentials: 'include' })
    .then((response): AvailableCredit => ({
      balance: parseFloat(response.metadata.available_credit),
      credits: response.result.map(creditMap),
      nextExpiringCredit: response.metadata.next_expiring,
    }))
}

function getOrderItemFlightDetails({ orderId, itemId }: { orderId: string, itemId: string }) {
  return request.get<App.ApiResponse<any>>(`/api/orders/${orderId}/items/${itemId}/flight-details`, { credentials: 'include' })
    .then(resp => flightDetailsMap(resp.result))
}

// Shared cache for in-flight flight detail requests to prevent duplicate API calls.
// Flight details are sometimes fetched outside the Redux flow to augment data.
// On some pages (e.g. My Escapes) the same request is triggered multiple times,
// so this cache works around that anti-pattern by deduping concurrent requests.
// We occasionally need this outside-Redux path—for example analytics needs
// up-to-date data before sending Snowplow events and cannot wait for the
// asynchronous Redux lifecycle—so the cache keeps those ad-hoc calls efficient.
const flightDetailsRequestCache = new Map<string, Promise<App.FlightOrderItemDetails>>()
export function getFlightDetailsWithDedup({ orderId, itemId, skipCache = false }: { orderId: string, itemId: string, skipCache?: boolean }): Promise<App.FlightOrderItemDetails> {
  const cacheKey = `${orderId}:${itemId}`

  // If skipCache is true, clear stale cache so fresh data gets cached for subsequent callers
  if (skipCache) {
    flightDetailsRequestCache.delete(cacheKey)
  }

  const existingPromise = flightDetailsRequestCache.get(cacheKey)
  if (existingPromise) {
    return existingPromise
  }

  const promise = getOrderItemFlightDetails({ orderId, itemId })

  flightDetailsRequestCache.set(cacheKey, promise)
  return promise
}

export function flightDetailsMap(details: any): App.FlightOrderItemDetails {
  let financialInformation: any = {}

  if (details.booking_journey.financialInformation?.refund) {
    financialInformation = {
      financialInformation: {
        refund: details.booking_journey.financialInformation?.refund,
      },
    }
  }

  const reservationAddons = details.reservation_addons

  // Intentionally omit journey.selectedUpsellOption(s). Purchased fare-family UI reads
  // booking_journey.legs[].selectedFare (see getPurchasedLegFareFamily) — the upsell
  // snapshot can be missing or stale after Sabre refresh.
  return {
    id: details.id,
    bookingJourney: {
      bookingMetadata: {
        searchId: details.booking_journey.bookingMetadata.searchId,
      },
      legs: details.booking_journey.legs.map((leg: App.Leg) => ({
        ...leg,
        // Baggage bought after booking never lands in the frozen `booking_journey`, so it is folded
        // back in here — the one place both My Escapes and the checkout confirmation read through.
        addons: [...(leg.addons ?? []), ...buildLegBaggageAddons(leg.id, reservationAddons)],
        flights: leg.flights.map(flight => ({
          ...flight,
          hiddenStops: flight.hiddenStops?.filter((s: App.HiddenStop) => !!s.airportCode) ?? [],
          seats: filterJourneySeatsToConfirmed(flight.seats, flight.id, reservationAddons),
        })),
      })),
      ...financialInformation,
    },
    flightChangePenalties: details?.change_penalties,
    journeyFares: {
      ...details.journey.journeyFares,
      fares: details.journey.journeyFares.fares.map(journeyV2Map),
    },
    reservationInfo: details.journey.reservationInfo,
    travellers: details.travellers.map(travellerMap),
    providerBookingReference: details.provider_booking_reference,
    pnrId: details.pnr_id,
    viewType: details.journey.viewType ?? details.view_type,
    region: details.region,
    fareType: details.journey.fareType as FareType,
    tripType: details.trip_type,
    state: details.state,
    isTicketed: details.is_ticketed ?? details.isTicketed ?? false,
    refundPenalties: details.refund_penalties,
    metadata: details.metadata,
    emergencyContact: getEmergencyContact(details),
    currency: details.currency,
    reservationAddons: details.reservation_addons,
    upgradeOptions: details.upgrade_options,
    scheduleChange: flightScheduleChangeMap(details.scheduleChange),
  }
}

function getEmergencyContact(details: {
  emergency_first_name?: string
  emergency_last_name?: string
  emergency_phone_number?: string
  emergency_relationship?: string
}): Omit<App.FlightEmergencyContact, 'countryCode'> | undefined {
  const {
    emergency_first_name,
    emergency_last_name,
    emergency_phone_number,
    emergency_relationship,
  } = details

  if (
    !emergency_first_name ||
    !emergency_last_name ||
    !emergency_phone_number ||
    !emergency_relationship
  ) {
    return
  }

  return {
    firstName: emergency_first_name,
    lastName: emergency_last_name,
    phone: emergency_phone_number,
    relationship: emergency_relationship,
  }
}

function travellerMap(traveller: any) {
  // TODO: do this better
  return traveller
}

export function transformKlarnaPayloadToServer(payload: KlarnaPayloadClient): KlarnaPayloadServer {
  return {
    attachment: payload.attachment,
    locale: payload.locale,
    purchase_country: payload.purchaseCountry,
    purchase_currency: payload.purchaseCurrency,
    // billing_address: {
    //   email: payload.billingAddress.email,
    //   family_name: payload.billingAddress.familyName,
    //   given_name: payload.billingAddress.givenName,
    //   phone: payload.billingAddress.phone,
    // },
    customer: {
      date_of_birth: payload.customer.dateOfBirth,
    },
    order_amount: payload.orderAmount,
    order_tax_amount: payload.orderTaxAmount,
    order_lines: payload.orderLines.map(line => ({
      type: line.type,
      reference: line.reference,
      name: line.name,
      quantity: line.quantity,
      unit_price: line.unitPrice,
      tax_rate: line.taxRate,
      total_amount: line.totalAmount,
      total_discount_amount: line.totalDiscountAmount,
      total_tax_amount: line.totalTaxAmount,
      product_url: line.productUrl,
      image_url: line.imageUrl,
    })),
  }
}

function datesRequestMap(datesRequest: any): App.DatesRequest {
  return {
    id: datesRequest.id_dates_request,
    customerId: datesRequest.fk_customer_id,
    orderId: datesRequest.fk_order_id,
    itemId: datesRequest.fk_item_id,
    packageId: datesRequest.fk_package_id,
    vendorId: datesRequest.vendor_salesforce_id,
    roomName: datesRequest.room_name,
    requestDate: datesRequest.request_date,
    numberOfAdults: datesRequest.number_of_adults,
    numberOfInfants: datesRequest.number_of_infants,
    numberOfChildren: datesRequest.number_of_children,
    numberOfTeenagers: datesRequest.number_of_teenagers ?? 0,
    createdAt: datesRequest.created_at,
    updatedAt: datesRequest.updated_at,
    status: datesRequest.status,
    type: datesRequest.type,
    reason: datesRequest.reason,
    reasonClassification: datesRequest.reason_classification,
    isOutsidePolicy: datesRequest.is_outside_policy,
    rejectionMessage: datesRequest.rejection_message,
    requestedNumberOfNights: datesRequest.requested_number_of_nights ?? undefined,
    requestedPackageId: datesRequest.requested_package_id ?? undefined,
    requestedRoomRateId: datesRequest.requested_room_rate_id ?? undefined,
  }
}

interface OrderRefundDetails {
  [id: string]: {
    refundAmount: number
    refundFee: number
  }
}

export function getOrderItemsRefundDetails(orderItemIds: Array<string>): Promise<Array<App.OrderItemRefundDetails>> {
  const query = qs.stringify({
    item_ids: orderItemIds.join(','),
  })
  return request.get<App.ApiResponse<Array<OrderRefundDetails>>>(`/api/orders/items/refund-policies?${query}`, { credentials: 'include' })
    .then(res => res.result.map(res => {
      const [orderItemId, details] = Object.entries(res)[0]
      return {
        ...details,
        orderItemId,
      }
    }))
}

export function getOrderItemRefundDetails(orderItemId: string): Promise<App.OrderItemRefundDetails> {
  return request.get<App.ApiResponse<OrderRefundDetails>>(`/api/orders/items/${orderItemId}/refund-policies`, { credentials: 'include' })
    .then(res => ({
      ...res.result[orderItemId],
      orderItemId,
    }))
}

export function redeemGiftRequest(code: string, orderId: string) {
  const uri = '/api/orders/redeem'
  return request.post<App.ApiResponse<OrderTypes.Order>, unknown>(uri, { id_order: orderId, redeem_code: code }, { credentials: 'include' })
    .then(response => orderMap(response.result))
}

export function markExperienceItemAsRedeemed(experienceItemId: string) {
  return request.patch(`/api/orders/experiences/items/booking-details/${experienceItemId}/mark-as-redeemed`, {}, { credentials: 'include' })
}

export function getTaxInvoiceDetails(orderId: string): Promise<App.OrderTaxInvoiceDetails> {
  return request.get(`/api/orders/${orderId}/tax-invoice-details`, { credentials: 'include' })
}

interface RefundCustomOfferProps {
  orderId: string
  itemId: string
  method: App.RefundMethod
  customerRefundReason: string
}

interface RefundCustomOfferToOriginalProps extends Omit<RefundCustomOfferProps, 'method' | 'customerRefundReason'> { }

async function refundCustomOfferToOriginal({
  orderId,
  itemId,
}: RefundCustomOfferToOriginalProps) {
  // back to origin will not refund anything automatically
  const uri = `/api/orders/${orderId}/items/${itemId}/cancel`
  const data = {
    op: 'cancel_custom_order_self_service',
  }
  await request.post(uri, data, { credentials: 'include' })
}

async function refundCustomOfferToCredit({
  orderId,
  itemId,
  method,
  customerRefundReason,
}: RefundCustomOfferProps) {
  const uri = `/api/orders/${orderId}/items/${itemId}?room_id=null`
  let comment = REFUND_BACK_TO_ORIGIN_COMMENT
  if (customerRefundReason) {
    comment = CUSTOM_OFFER_CANCELLATION_REASON_COMMENT_BY_CODE[customerRefundReason] ??
      (REFUND_REASONS.find((r) => r === customerRefundReason) ?
        customerRefundReason :
        `Other - {${customerRefundReason}}`)
  }
  const data: App.RefundRequestData = {
    op: 'refund',
    transaction_key: uuidV4(),
    reason: 'Custom Offer automatic refund',
    comment,
    method,
  }

  await request.put(uri, data, { credentials: 'include' })
}

export async function refundCustomOffers({
  orderId,
  itemId,
  method,
  customerRefundReason,
}: RefundCustomOfferProps): Promise<App.Order> {
  if (method === 'back_to_origin') {
    // this will create the salesforce case without
    await refundCustomOfferToOriginal({
      orderId,
      itemId,
    })
  } else {
    // this will refund the credits automatically
    await refundCustomOfferToCredit({
      orderId,
      itemId,
      method,
      customerRefundReason,
    })
  }
  // when we call refundCustomOffers, there is a slight delay between the update due to jobs being created.
  // So sometimes we call getOrderWithPaymentsAndReservations but some of these jobs could not be finished yet.
  // When this happens we need to wait a few moments and call getOrderWithPaymentsAndReservations again.
  // This function does it
  await asyncPoll<App.Order>({
    validateFunction: async(order) => {
      const item = order.customOfferItems.find(updatedItem => updatedItem.id === itemId)
      return !!item && item.status === 'cancelled'
    },
    apiCall: () => getOrderById(orderId),
    maxTime: 30000,
    pollInterval: 500,
  })

  return await getFullOrder(orderId)
}

export function submitInsuranceClaim(orderId: string, itemId: string) {
  return request.put(`/api/orders/${orderId}/items/${itemId}`, {
    op: 'insurance_claim_submitted',
  }, { credentials: 'include' }).then(() => {
    return getFullOrder(orderId)
  })
}

export type SpecialRequestStatus =
  | 'submitted'
  | 'failed'
  | 'unsupported'
  | 'pending'
  | 'confirmed'
  | 'declined'

export async function updateGuestSpecialRequests(
  orderId: string,
  itemId: string,
  guestSpecialRequests: string,
  roomId?: string,
) {
  const body = {
    guest_special_requests: guestSpecialRequests,
    op: 'change_guest_special_requests',
    cn: '',
    transaction_key: uuidV4(),
    ...(roomId ? { room_id: roomId } : {}),
  }

  return request
    .put<App.ApiResponse<any>, any>(
      `/api/orders/${orderId}/items/${itemId}`,
      body,
      { credentials: 'include' },
    ).then((response) => ({
      result: response.result as {
        guest_special_requests: string
        special_request_status?: SpecialRequestStatus
        room_id?: string
      },
      orderId,
      itemId,
      roomId,
    }))
}

export type GuestNameChangeStatus =
  | 'pending'
  | 'confirmed'
  | 'failed'
  | 'unsupported'
  | 'chargeable'

interface UpdateGuestNameParams {
  orderId: string
  itemId: string
  guestFirstName: string
  guestLastName: string
  roomId?: string
  otpCode?: string
  insuranceDetails?: Omit<App.Checkout.UpdatedInsuranceDetails, 'transactionKey'> & {
    transactionKey?: string
  }
}

export async function updateGuestName({
  orderId,
  itemId,
  guestFirstName,
  guestLastName,
  roomId,
  otpCode,
  insuranceDetails,
}: UpdateGuestNameParams): Promise<{
  order: App.Order
  guestNameChangeStatus?: GuestNameChangeStatus
}> {
  const transactionKey = uuidV4()
  const body = {
    op: 'change_guest_details',
    cn: '',
    transaction_key: transactionKey,
    guest_first_name: guestFirstName,
    guest_last_name: guestLastName,
    ...(roomId && { room_id: roomId }),
    ...(otpCode && { otp_code: otpCode }),
    ...(insuranceDetails && {
      insuranceDetails: {
        ...insuranceDetails,
        transactionKey: insuranceDetails.transactionKey ?? transactionKey,
      },
    }),
  }

  const response = await request.put<App.ApiResponse<{
    guest_name_change_status?: GuestNameChangeStatus
  }>, typeof body>(
    `/api/orders/${orderId}/items/${itemId}`,
    body,
    { credentials: 'include' },
  )

  return {
    order: await getFullOrder(orderId),
    guestNameChangeStatus: response.result?.guest_name_change_status,
  }
}

export function updateOrderCarHireIntention(orderId: string, hasCarHireIntention: boolean) {
  return request.patch(`/api/orders/${orderId}`, {
    op: 'update_order_car_hire_intention',
    has_car_hire_intention: hasCarHireIntention,
  }, { credentials: 'include' })
}

export function fetchOrderAccessInvites(orderId: string, status?: ApiOrderAccessInviteStatus) {
  const params: Record<string, string> = {}

  if (status) {
    params.status = status
  }
  const queryString = new URLSearchParams(params).toString()

  return request.get(
    `/api/orders/${orderId}/invitation?${queryString}`,
    { credentials: 'include' },
  )
}

export function createOrderAccessInvites(
  orderId: string,
  body: CreateOrderAccessInvitesRequest,
): Promise<Array<App.CollaboratorEntry>> {
  return request
    .post<Array<App.CollaboratorEntry>, CreateOrderAccessInvitesRequest>(
      `/api/orders/${orderId}/invitation`,
      body,
      { credentials: 'include' },
    )
}

export function acceptOrderAccessInvite(
  orderId: string,
  invitationId: string,
): Promise<App.OrderAccessInvite> {
  return request.patch<App.OrderAccessInvite, undefined>(
    `/api/orders/${orderId}/invitation/${invitationId}/accept`,
    undefined,
    { credentials: 'include' },
  )
}

export function fetchOrderAccessInvite(orderId: string, invitationId: string): Promise<App.OrderAccessInvite> {
  return request.get<App.OrderAccessInvite>(
    `/api/orders/${orderId}/invitation/${invitationId}`,
    { credentials: 'include' },
  )
}

export function updateOrderAccessInvite(orderId: string, invitationId: string): Promise<App.OrderAccessInvite> {
  return request.patch<App.OrderAccessInvite, undefined>(
    `/api/orders/${orderId}/invitation/${invitationId}/cancel`,
    undefined,
    { credentials: 'include' },
  )
}

export function fetchOrderAccessList(orderId: string, query?: FetchOrderAccessListQueryParams) {
  const params: Record<string, string> = {}

  if (query?.status) {
    params.status = query.status
  }

  if (query?.role) {
    params.role = query.role
  }

  const queryString = new URLSearchParams(params).toString()

  return request.get(
    `/api/orders/${orderId}/access?${queryString}`,
    { credentials: 'include' },
  )
}

export function updateOrderAccess(orderId: string, orderAccessId: string, data: Partial<App.OrderAccess>) {
  return request.patch(
    `/api/orders/${orderId}/access/${orderAccessId}`,
    data,
    { credentials: 'include' },
  )
}

export interface GainOrderAccessByPinPayload {
  bookingId: string
  pin: string
  brand: App.BrandName
}

export interface GainOrderAccessByPinResponse {
  orderId: string
  access?: App.OrderAccess
}

export function gainOrderAccessByPin(payload: GainOrderAccessByPinPayload) {
  return request.post<GainOrderAccessByPinResponse, GainOrderAccessByPinPayload>(
    '/api/orders/access/booking-pin',
    payload,
    { credentials: 'include' },
  )
}

export async function fetchMyOrderAccess(orderId: string) {
  return request.get<{ result: App.MyOrderAccess }>(
    `/api/orders/${orderId}/access/me`,
    { credentials: 'include' },
  ).then(response => response.result)
    .catch(err => {
      console.error('Failed to fetch MyOrderAccess', err)
      throw err
    })
}

export function revokeMyOrderAccess(orderId: string) {
  return request.post(
    `/api/orders/${orderId}/access/me/revoke`,
    undefined,
    { credentials: 'include' },
  )
}

export function getCruiseDetails(
  { orderId, itemId }: { orderId: string, itemId: string },
  regionCode: string,
): Promise<App.Cruises.CruiseBooking> {
  return getOrderItemCruiseDetails({ orderId, itemId }, regionCode)
}

export function getCruisePassengerAmendmentConfig(
  { orderId, itemId }: { orderId: string, itemId: string },
): Promise<App.Cruises.CruisePassengerAmendmentConfig> {
  return request.get<App.ApiResponse<App.Cruises.CruisePassengerAmendmentConfig>>(
    `/api/orders/${orderId}/items/${itemId}/amendment-config`,
    { credentials: 'include' },
  ).then(response => response.result)
}

/**
 * Accepted asynchronously (202): svc-order applies the change with the provider in a job
 * and completes the transaction under `transaction_key` after provider and any required payment confirmation.
 */
export async function amendCruisePassengers(orderId: string, itemId: string, data: AmendCruisePassengersData): Promise<CruiseAmendmentSubmissionResult> {
  const plan = data.payments
  const payload = {
    transaction_key: data.transaction_key,
    passengers: data.passengers,
    ...(data.otp_code && { otp_code: data.otp_code }),
    ...(plan && {
      payment_details: {
        payment_plan_key: plan.payment_plan_key,
        currency: plan.currency,
        // Shared checkout may populate this for full payments; amendments never use deposits.
        depositAmount: 0,
        isDepositPayment: plan.isDepositPayment,
        isDeferredPayment: plan.isDeferredPayment,
        payments: plan.payments.map(payment => ({
          currency: payment.currency ?? plan.currency,
          amount: payment.amount,
          type: payment.type,
          external_ref: payment.external_ref,
          isDepositPayment: payment.isDepositPayment,
          isDeferredPayment: payment.isDeferredPayment,
          additional_info: {
            isSecurePayment: payment.additional_info?.isSecurePayment,
            merchantFeeInfo: payment.additional_info?.merchantFeeInfo,
            isDepositPayment: payment.additional_info?.isDepositPayment,
          },
        })),
      },
    }),
  }
  try {
    await request.post<App.ApiResponse<unknown>, unknown>(
      `/api/orders/${orderId}/items/${itemId}/passenger-amendments`,
      payload,
      { credentials: 'include', excludeBrand: true },
    )
    return { status: 'accepted' }
  } catch(error) {
    const otpError = getCruiseAmendmentOtpError(error)
    if (otpError) return { status: otpError }
    throw error
  }
}

export interface ApiSuggestedCollaborator {
  name: string
  email: string
}

export function fetchSuggestedCollaborators(): Promise<Array<ApiSuggestedCollaborator>> {
  return request.get<{ result: Array<ApiSuggestedCollaborator> }>(
    '/api/orders/suggested-collaborators',
    { credentials: 'include' },
  ).then(resp => resp.result)
}

export interface FlashUpgradeRecommendation {
  offerId: string
  offerType: string
  name: string
  url: string
  distanceKm: number
  price: number
  currency: string
  checkIn: string
  checkOut: string
  adults: number
  children: number
  infants: number
  inclusionsCount: number
}

export function getFlashUpgradeRecommendations(
  orderId: string,
): Promise<{ recommendations: Array<FlashUpgradeRecommendation> }> {
  return request.get(`/api/orders/${orderId}/flash-upgrade-recommendations`, { credentials: 'include' })
}

function getOrderItemCruiseDetails({ orderId, itemId }: { orderId: string, itemId: string }, regionCode: string) {
  return request.get<OrderTypes.CruiseDetailsResponse>(
    `/api/orders/${orderId}/items/${itemId}/cruise-details`,
    { credentials: 'include' },
  ).then(resp => cruiseBookingMap(resp.result, regionCode))
}

export function applyCharterUpgrade({ pnr, toCabinClass }: { pnr: string, toCabinClass: string }) {
  return request.patch(
    `/api/flights/charters/${pnr}/fare-class`,
    { toCabinClass },
    { credentials: 'include' },
  )
}
